import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from '../../hooks/use-toast';
import { ClipboardText, Check, Briefcase, CaretDown, CaretUp, Camera, Note, Microphone } from '@phosphor-icons/react';
import TaskCompletionModal from './TaskCompletionModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const todayISO = () => new Date().toISOString().slice(0, 10);

/**
 * Worker-side "Today's Tasks" widget. Reads checklists assigned to the current
 * worker for today. Tap a task to mark it done — a modal lets the worker
 * optionally add a note + photo proof. Tap a done task to view/edit the proof.
 * Contractor is notified server-side.
 */
const WorkerTodayChecklists = () => {
  const [checklists, setChecklists] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalCtx, setModalCtx] = useState(null); // { checklistId, task, readOnly }

  const fetchLists = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/daily-checklists?date=${todayISO()}`, authHeader());
      const items = res.data?.checklists || [];
      setChecklists(items);
      // Auto-expand the first item if only one
      if (items.length === 1) setExpandedId(items[0].id);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLists(); }, [fetchLists]);
  useEffect(() => {
    const id = setInterval(fetchLists, 20000);
    return () => clearInterval(id);
  }, [fetchLists]);

  const applyChecklist = (updated) => {
    setChecklists((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
  };

  // Direct un-check (no modal — just clears the task)
  const uncheck = async (checklistId, taskId) => {
    try {
      const res = await axios.post(
        `${API}/daily-checklists/${checklistId}/tasks/${taskId}/toggle`,
        { done: false },
        authHeader(),
      );
      applyChecklist(res.data.checklist);
    } catch {
      toast({ title: 'Update failed', variant: 'destructive' });
    }
  };

  // Handle a click on a task row: open modal for note/photo (or view proof if done)
  const openTaskModal = (checklistId, task) => {
    setModalCtx({ checklistId, task, readOnly: task.done });
  };

  const submitCompletion = async (payload) => {
    if (!modalCtx) return;
    try {
      const res = await axios.post(
        `${API}/daily-checklists/${modalCtx.checklistId}/tasks/${modalCtx.task.id}/toggle`,
        payload,
        authHeader(),
      );
      applyChecklist(res.data.checklist);
      toast({ title: 'Task marked done', description: 'Contractor has been notified.' });
    } catch (err) {
      toast({ title: 'Update failed', description: err?.response?.data?.detail || 'Try again', variant: 'destructive' });
    }
  };

  if (loading) return null;
  const empty = checklists.length === 0;

  return (
    <div className="bg-white dark:bg-neutral-900 border-4 border-black shadow-[6px_6px_0px_#000] p-4 md:p-5" data-testid="worker-today-checklists">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg md:text-xl font-black uppercase tracking-tight flex items-center gap-2">
          <ClipboardText size={22} weight="fill" className="text-[#475569]" />
          Today&apos;s Tasks
          <span
            className="inline-flex items-center gap-1 bg-black text-[#475569] text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 border border-[#3B82F6]"
            data-testid="worker-checklists-voice-badge"
          >
            <Microphone size={10} weight="fill" /> Voice
          </span>
        </h2>
        {!empty && (
          <span className="text-[10px] font-black uppercase tracking-widest bg-[#3B82F6] text-[#0F172A] px-2 py-0.5 border-2 border-black">
            {checklists.length} {checklists.length === 1 ? 'list' : 'lists'}
          </span>
        )}
      </div>

      {empty ? (
        <div className="flex items-start gap-3" data-testid="worker-checklists-empty">
          <div className="w-10 h-10 flex-shrink-0 bg-black border-2 border-[#3B82F6] flex items-center justify-center">
            <Microphone size={18} weight="fill" className="text-[#475569]" />
          </div>
          <div>
            <p className="text-sm font-black text-neutral-900 dark:text-white">No tasks assigned yet today.</p>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 leading-relaxed mt-0.5">
              When your contractor sends a daily checklist it lands right here. Tap-to-check each task,
              attach photo proof, and dictate completion notes hands-free — English or Spanish.
            </p>
          </div>
        </div>
      ) : (
      <ul className="space-y-3">
        {checklists.map((c) => {
          const tasks = c.tasks || [];
          const done = tasks.filter(t => t.done).length;
          const pct = tasks.length ? Math.round((done / tasks.length) * 100) : 0;
          const isOpen = expandedId === c.id;

          return (
            <li key={c.id} className="border-2 border-black bg-neutral-50 dark:bg-neutral-800" data-testid={`worker-checklist-${c.id}`}>
              <button
                onClick={() => setExpandedId(isOpen ? null : c.id)}
                className="w-full p-3 flex items-center justify-between text-left hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
                data-testid={`worker-checklist-toggle-${c.id}`}
              >
                <div className="min-w-0 flex-1 pr-2">
                  <div className="text-[10px] font-black uppercase tracking-widest text-[#475569] mb-0.5 flex items-center gap-1 truncate">
                    <Briefcase size={11} weight="fill" /> {c.job_title}
                  </div>
                  <div className="font-black text-sm truncate">{c.title || 'Daily Tasks'}</div>
                  <div className="text-xs text-neutral-600 dark:text-neutral-400 flex items-center gap-1 mt-0.5">
                    <span>{done}/{tasks.length} done</span>
                    <span className="ml-2 font-bold">{pct}%</span>
                  </div>
                </div>
                <div className="flex-shrink-0 flex items-center gap-2">
                  {pct === 100 && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest bg-emerald-500 text-white px-2 py-0.5 border-2 border-black">
                      <Check size={10} weight="bold" /> Done
                    </span>
                  )}
                  {isOpen ? <CaretUp size={16} weight="bold" /> : <CaretDown size={16} weight="bold" />}
                </div>
              </button>

              {isOpen && (
                <div className="p-3 border-t-2 border-black">
                  {/* Progress bar */}
                  <div className="h-2 bg-neutral-200 dark:bg-neutral-700 border border-black overflow-hidden mb-3">
                    <div
                      className={`h-full ${pct === 100 ? 'bg-emerald-500' : 'bg-[#3B82F6]'} transition-all`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>

                  {tasks.length === 0 ? (
                    <p className="text-xs text-neutral-500 italic">No tasks in this list.</p>
                  ) : (
                    <ul className="space-y-2">
                      {tasks.map((t) => {
                        const hasNote = !!t.note;
                        const photoCount = (t.photo_ids || []).length;
                        return (
                          <li key={t.id}>
                            <div
                              className={`w-full flex items-start gap-3 p-2 border-2 transition-colors ${
                                t.done
                                  ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-500'
                                  : 'bg-white dark:bg-neutral-900 border-black'
                              }`}
                              data-testid={`worker-task-row-${t.id}`}
                            >
                              {/* Toggle box */}
                              {t.done ? (
                                <button
                                  onClick={() => uncheck(c.id, t.id)}
                                  className="flex-shrink-0 w-6 h-6 mt-0.5 border-2 border-black flex items-center justify-center bg-emerald-500 hover:bg-emerald-600"
                                  data-testid={`worker-task-uncheck-${t.id}`}
                                  title="Un-check"
                                >
                                  <Check size={16} weight="bold" className="text-white" />
                                </button>
                              ) : (
                                <button
                                  onClick={() => openTaskModal(c.id, t)}
                                  className="flex-shrink-0 w-6 h-6 mt-0.5 border-2 border-black flex items-center justify-center bg-white dark:bg-neutral-800 hover:bg-neutral-100"
                                  data-testid={`worker-task-check-${t.id}`}
                                  title="Mark done"
                                />
                              )}

                              {/* Row body — clickable to open modal (view proof or add proof) */}
                              <button
                                onClick={() => openTaskModal(c.id, t)}
                                className="flex-1 min-w-0 text-left"
                                data-testid={`worker-task-open-${t.id}`}
                              >
                                <span className={`text-sm leading-snug block ${t.done ? 'line-through text-neutral-400' : 'text-neutral-800 dark:text-neutral-200'}`}>
                                  {t.text}
                                </span>
                                {(hasNote || photoCount > 0) && (
                                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                                    {photoCount > 0 && (
                                      <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest bg-black text-white px-1.5 py-0.5">
                                        <Camera size={10} weight="fill" /> {photoCount}
                                      </span>
                                    )}
                                    {hasNote && (
                                      <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest bg-neutral-200 dark:bg-neutral-700 dark:text-white px-1.5 py-0.5 border border-black">
                                        <Note size={10} weight="fill" /> Note
                                      </span>
                                    )}
                                  </div>
                                )}
                              </button>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
      )}

      {modalCtx && (
        <TaskCompletionModal
          task={modalCtx.task}
          readOnly={modalCtx.readOnly}
          onSubmit={submitCompletion}
          onClose={() => setModalCtx(null)}
        />
      )}
    </div>
  );
};

export default WorkerTodayChecklists;
